import { apiClient, toCamelCase, toSnakeCase } from "./client";
import type {
  DocumentTemplate,
  DocumentTemplateCreateInput,
  DocumentTemplateSection,
  DocumentTemplateSectionCreateInput,
  DocumentTemplateSectionUpdateInput,
  DocumentTemplateSummary,
  DocumentTemplateUpdateInput,
  PromptTestResponse,
  TemplatePromptTestRequest,
} from "@/types";

export const documentTemplatesApi = {
  list: async (includeInactive = false): Promise<DocumentTemplateSummary[]> => {
    const response = await apiClient.get("/document-templates", {
      params: { include_inactive: includeInactive },
    });
    return (toCamelCase(response.data) as DocumentTemplateSummary[]) || [];
  },

  get: async (id: number): Promise<DocumentTemplate> => {
    const response = await apiClient.get(`/document-templates/${id}`);
    return toCamelCase(response.data) as DocumentTemplate;
  },

  create: async (data: DocumentTemplateCreateInput): Promise<DocumentTemplate> => {
    const response = await apiClient.post("/document-templates", toSnakeCase(data));
    return toCamelCase(response.data) as DocumentTemplate;
  },

  clone: async (
    sourceId: number,
    data: { key: string; name: string }
  ): Promise<DocumentTemplate> => {
    const response = await apiClient.post(
      "/document-templates",
      toSnakeCase(data),
      { params: { clone_from: sourceId } }
    );
    return toCamelCase(response.data) as DocumentTemplate;
  },

  update: async (
    id: number,
    data: DocumentTemplateUpdateInput
  ): Promise<DocumentTemplate> => {
    const response = await apiClient.put(`/document-templates/${id}`, toSnakeCase(data));
    return toCamelCase(response.data) as DocumentTemplate;
  },

  remove: async (id: number): Promise<void> => {
    await apiClient.delete(`/document-templates/${id}`);
  },

  // sections
  addSection: async (
    templateId: number,
    data: DocumentTemplateSectionCreateInput
  ): Promise<DocumentTemplateSection> => {
    const response = await apiClient.post(
      `/document-templates/${templateId}/sections`,
      toSnakeCase(data)
    );
    return toCamelCase(response.data) as DocumentTemplateSection;
  },

  updateSection: async (
    templateId: number,
    sectionId: number,
    data: DocumentTemplateSectionUpdateInput
  ): Promise<DocumentTemplateSection> => {
    const response = await apiClient.put(
      `/document-templates/${templateId}/sections/${sectionId}`,
      toSnakeCase(data)
    );
    return toCamelCase(response.data) as DocumentTemplateSection;
  },

  deleteSection: async (templateId: number, sectionId: number): Promise<void> => {
    await apiClient.delete(`/document-templates/${templateId}/sections/${sectionId}`);
  },

  reorderSections: async (
    templateId: number,
    sectionIds: number[]
  ): Promise<DocumentTemplateSection[]> => {
    const response = await apiClient.post(
      `/document-templates/${templateId}/sections/reorder`,
      { section_ids: sectionIds }
    );
    return toCamelCase(response.data) as DocumentTemplateSection[];
  },

  testTemplate: async (
    templateId: number,
    payload: TemplatePromptTestRequest
  ): Promise<PromptTestResponse> => {
    const response = await apiClient.post(
      `/document-templates/${templateId}/test`,
      toSnakeCase(payload)
    );
    return toCamelCase(response.data) as PromptTestResponse;
  },

  testSection: async (
    templateId: number,
    sectionId: number,
    payload: TemplatePromptTestRequest
  ): Promise<PromptTestResponse> => {
    const response = await apiClient.post(
      `/document-templates/${templateId}/sections/${sectionId}/test`,
      toSnakeCase(payload)
    );
    return toCamelCase(response.data) as PromptTestResponse;
  },
};
